// Section 24.14: actions that edit the decision draft. Allowed only while deciding (24.7).
import {
  isRetired,
  steward,
  type Decision,
  type JobId,
  type PersonId,
  type PolicyId,
  type Policies,
} from '../../sim/api';
import { withBoundary } from '../boundary';
import { requireGame, requireStatus } from '../guards';
import { runConfig } from '../run';
import { draft, patchUi, ui } from '../state';

/** Replace the draft with an edited copy (deciding only). Other streams may reuse this. */
export function editDraft(edit: (d: Decision) => Decision): void {
  requireStatus('deciding');
  const current = draft.value;
  if (current === null) return;
  draft.value = edit(current);
  patchUi({ problems: [] });
}

export const assignJob = withBoundary((personId: PersonId, jobId: JobId): void => {
  const state = requireGame();
  editDraft((d) => {
    const assignments = { ...d.assignments };
    if (state.people[personId]?.job === jobId) delete assignments[personId];
    else assignments[personId] = jobId;
    return { ...d, assignments };
  });
});

export const setPolicy = withBoundary(<K extends PolicyId>(id: K, value: Policies[K]): void => {
  const state = requireGame();
  editDraft((d) => {
    const policies = { ...d.policies };
    // Setting a policy back to its standing value removes the change (only changes are logged).
    if (JSON.stringify(state.policies[id]) === JSON.stringify(value)) delete policies[id];
    else policies[id] = value;
    return { ...d, policies };
  });
});

// Section 9 build actions live with the Build tab's actions (stream M3a).
export { cancelBuild, queueBuild } from './build';

export const selectPerson = (personId: PersonId | null): void => {
  patchUi({ selectedPersonId: personId });
};

/** Section 3: the player may rename anyone (Decision.renames). Blank reverts the change. */
export const renamePerson = withBoundary((personId: PersonId, name: string): void => {
  const state = requireGame();
  const trimmed = name.trim();
  editDraft((d) => {
    const renames = { ...d.renames };
    if (trimmed === '' || trimmed === state.people[personId]?.name) delete renames[personId];
    else renames[personId] = trimmed;
    return { ...d, renames };
  });
});

/** Effective job this season: the draft's change, else the standing job. */
function jobIn(d: Decision, personId: PersonId, standing: JobId): JobId {
  return d.assignments[personId] ?? standing;
}

/**
 * Section 22 Jobs: auto-fill proposes assignments that cover needs using current skills (the
 * steward's plan). It only runs on a press and shows the diff; applyAutoFill applies it.
 */
export const proposeAutoFill = withBoundary((): void => {
  requireStatus('deciding');
  const state = requireGame();
  const d = draft.value;
  if (d === null) return;
  const cfg = runConfig();
  const plan = steward(state, cfg).assignments;
  const assignments: Record<PersonId, JobId> = {};
  for (const [id, job] of Object.entries(plan) as [PersonId, JobId][]) {
    const p = state.people[id];
    // Section 18 retirement age: retired people are never auto-assigned (they may volunteer).
    if (p === undefined || isRetired(state, p, cfg)) continue;
    if (jobIn(d, id, p.job) !== job) assignments[id] = job;
  }
  if (Object.keys(assignments).length === 0) {
    patchUi({ autoFill: null, notice: { code: 'autofill-empty', detail: null } });
    return;
  }
  patchUi({ autoFill: { turn: state.turn, assignments } });
});

export const applyAutoFill = withBoundary((): void => {
  const state = requireGame();
  const proposal = ui.value.autoFill;
  if (proposal === null || proposal.turn !== state.turn) {
    patchUi({ autoFill: null });
    return;
  }
  editDraft((d) => {
    const assignments = { ...d.assignments };
    for (const [id, job] of Object.entries(proposal.assignments) as [PersonId, JobId][]) {
      if (state.people[id]?.job === job) delete assignments[id];
      else assignments[id] = job;
    }
    return { ...d, assignments };
  });
  patchUi({ autoFill: null });
});

export const discardAutoFill = (): void => {
  patchUi({ autoFill: null });
};
